/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { globalAlert, globalLoader } from "../../actions/commonActions";
import ConfirmationModal from "../_common/confirmModal";
import LmsAssetList from "./lmsAssetList";
import LmsQuizForm from "./lmsQuizForm";
import { assetPayload, assetIdOf } from "../../admin/_lmsHelpers";
import {
  lmsAddSectionApi,
  lmsDeleteSectionApi,
  lmsReorderSectionsApi,
  lmsAddSectionAssetApi,
  lmsUpdateSectionAssetApi,
  lmsDeleteSectionAssetApi,
} from "../../admin/_lmsActions";

const sid = (s) => s?._id || s?.sectionId;

// Single-section editor — the media flow (ordered asset list) + the section
// quiz, wired to the granular section-asset routes so section ids stay stable.
// Reused by the accordion SectionRow and the tree-based module editor.
//
// `part` splits the Part into its authoring surfaces so the tree editor can
// show video, optional summary slide and quiz as separate nodes. Undefined
// renders all surfaces (the accordion still uses that).
export const LmsSectionEditor = ({
  moduleId,
  moduleTitle,
  section,
  onRefresh,
  showFlowHint = true,
  part,
  uploadTitle,
  uploadContext = {},
  registerUnsavedChangesGuard,
  setWizardDirty,
}) => {
  const { t } = useTranslation();
  const id = sid(section);
  const assets = (section?.assets || [])
    .slice()
    .sort((a, b) => (a?.order || 0) - (b?.order || 0));
  const videoAssets = assets.filter((asset) => asset?.type === "video");
  const summaryAssets = assets.filter((asset) => asset?.type === "pdf");
  const sectionTitle = uploadTitle?.trim?.() || section?.title?.trim?.() || "";
  const quiz = section?.quizDoc || (section?.quiz ? { _id: section.quiz } : null);
  const [toDeleteAsset, setToDeleteAsset] = useState(null);


  const run = async (fn) => {
    try {
      globalLoader(true);
      const res = await fn();
      globalLoader(false);
      if (res?.data?.status === 200) onRefresh?.();
      else globalAlert("error", res?.data?.message);
    } catch (e) {
      globalLoader(false);
      globalAlert("error", e?.response?.data?.message);
    }
  };

  const resequenceAssets = async (ordered) => {
    let lastRes = null;
    for (let i = 0; i < ordered.length; i++) {
      const a = ordered[i];
      if (a.order !== i + 1 && assetIdOf(a)) {
        lastRes = await lmsUpdateSectionAssetApi(moduleId, id, assetIdOf(a), {
          ...assetPayload(a),
          order: i + 1,
        });
      }
    }
    return lastRes || { data: { status: 200 } };
  };
  const addMediaAsset = async (asset) => {
  await run(() =>
    lmsAddSectionAssetApi(moduleId, id, {
      ...assetPayload(asset),
      order: assets.length + 1,
    })
  );

  setWizardDirty?.(true);
};

const deleteMediaAsset = (asset) => {
  setToDeleteAsset({
    asset,
    kind: asset?.type || "asset",
  });
};
const confirmDeleteAsset = async (asset) => {
  await run(() => lmsDeleteSectionAssetApi(moduleId, id, assetIdOf(asset)));

  setWizardDirty?.(true);
};

  const reorderAssets = async (ordered) => {
    try {
      globalLoader(true);
      await resequenceAssets(ordered);
      globalLoader(false);
      setWizardDirty?.(true);
      onRefresh?.();
    } catch (e) {
      globalLoader(false);
      globalAlert("error", e?.response?.data?.message);
    }
  };

  // `part` unset ⇒ render both (accordion); otherwise just the requested surface.
  const showVideos = part == null || part === "media" || part === "video";
  const showSummary = part == null || part === "summary";
  const showQuiz = part == null || part === "quiz";

  return (
    <>
      {showVideos && (
        <>
          {/* One ordered video flow per Part. The heading is dropped when the tree already labels this node "Video". */}
          {part == null && (
            <h5 className="lms-subsection-title">{t("admin.lms.asset.heading")}</h5>
          )}
          {showFlowHint && (
            <p className="text-muted small mb-2">
              {t(
                "admin.lms.section.flowHint",
                "Add resources in the order learners should see them."
              )}
            </p>
          )}
          {showVideos && (
            <div>
              {part == null && (
                <h5 className="lms-subsection-title">
                  {t("admin.lms.tree.video", "Video")}
                </h5>
              )}
              <LmsAssetList
                assets={videoAssets}
                variant="partAsset"
                assetKind="video"
                accept="video/mp4,video/webm,video/quicktime"
                uploadTitle={sectionTitle}
                uploadContext={{
                  ...uploadContext,
                  assetRole: "section-asset",
                  sectionId: id,
                  sectionTitle,
                }}
                helperText={t(
                  "admin.lms.section.videoUploadHint",
                  "Add multiple videos for this part. Learners will be able to watch them in sequence."
                )}
                emptyTitle={t(
                  "admin.lms.section.noVideosUploaded",
                  "No videos yet."
                )}
                emptyBody={t(
                  "admin.lms.section.uploadFirstVideo",
                  "Upload your first video to get started."
                )}
                // recommendedText={t(
                //   "admin.lms.section.videoRecommended",
                //   "Recommended: MP4, WebM or MOV (Max. 500MB)"
                // )}
                onAdd={addMediaAsset}
                onDelete={deleteMediaAsset}
                onReorder={(ordered) => reorderAssets(ordered)}
                addLabel={t("admin.lms.asset.uploadVideo", "Upload Video")}
              />
            </div>
          )}
        </>
      )}

      {showSummary && (
        <div className={part == null ? "mt-4" : ""}>
          {part == null && (
            <h5 className="lms-subsection-title">
              {t("admin.lms.tree.partSummary", "Summary Slide")}{" "}
              <span className="lms-optional-chip">
                {t("common.optional", "Optional")}
              </span>
            </h5>
          )}
          <LmsAssetList
            assets={summaryAssets}
            variant="partAsset"
            assetKind="pdf"
            accept="application/pdf"
            maxItems={1}
            reorderable={false}
            uploadTitle={`${sectionTitle}-summary`}
            uploadContext={{
              ...uploadContext,
              assetRole: "section-summary",
              sectionId: id,
              sectionTitle,
            }}
            helperText={t(
              "admin.lms.section.summaryUploadHint",
              "Optional PDF summary for this part. It does not affect readiness or learner completion."
            )}
            emptyTitle={t(
              "admin.lms.section.noSummaryUploaded",
              "No summary slide added."
            )}
            emptyBody={t(
              "admin.lms.section.summaryOptionalHint",
              "You can continue without a summary slide."
            )}
            onAdd={addMediaAsset}
            onDelete={deleteMediaAsset}
            addLabel={t("admin.lms.section.uploadSummary", "Upload Summary Slide")}
          />
        </div>
      )}

      {showQuiz && (
        <>
          {part == null && (
            <h5 className="lms-subsection-title mt-3">
              {t("admin.lms.section.quizHeading")}
            </h5>
          )}
          <LmsQuizForm
            scope="section"
            refId={moduleId}
            sectionId={id}
            initialQuiz={quiz}
            moduleTitle={moduleTitle}
            sectionTitle={sectionTitle}
            onSaved={() => onRefresh?.()}
            registerUnsavedChangesGuard={registerUnsavedChangesGuard}
            setWizardDirty={setWizardDirty}
          />
        </>
      )}
                        {toDeleteAsset && (
                      <ConfirmationModal
                        open={!!toDeleteAsset}
                        deleteInfo={[
                      `${
                        toDeleteAsset.kind === "video"
                          ? t("admin.lms.asset.videoTitle", "Video")
                          : t("admin.lms.asset.fileTitle", "Summary Slide")
                      } : ${
                        toDeleteAsset.asset.title ||
                        toDeleteAsset.asset.filename ||
                        "-"
                      }`,
                    ]}

                    description={t(
                      toDeleteAsset.kind === "video"
                        ? "admin.lms.asset.confirmDeleteVideo"
                        : "admin.lms.asset.confirmDeleteFile",
                      toDeleteAsset.kind === "video"
                        ? "Are you sure you want to delete this video?"
                        : "Are you sure you want to delete this file?"
                    )}
                        onCloseModal={(ok) => {
                          if (ok) {
                            confirmDeleteAsset(toDeleteAsset.asset);
                          }
                          setToDeleteAsset(null);
                        }}
                      />
                    )}
    </>
  );
};

// Part 1..N accordion. Each section = details + an asset list (wired to the
// granular section-asset routes so section ids stay stable) + an inline
// section-scoped quiz. Sections add / delete / reorder in place.
const LmsSectionsPanel = ({ courseId, moduleId, moduleTitle, sections = [], onRefresh }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const sorted = [...sections].sort((a, b) => (a?.order || 0) - (b?.order || 0));
  const [openId, setOpenId] = useState(sorted[0] ? sid(sorted[0]) : null);
  const [confirm, setConfirm] = useState(null);

  const run = async (fn, { refresh = true } = {}) => {
    try {
      globalLoader(true);
      const res = await fn();
      globalLoader(false);
      if (res?.data?.status === 200) {
        if (refresh) onRefresh?.();
        return res;
      }
      globalAlert("error", res?.data?.message);
    } catch (e) {
      globalLoader(false);
      globalAlert("error", e?.response?.data?.message);
    }
    return null;
  };

  const addSection = async () => {
    const res = await run(
      () =>
        lmsAddSectionApi(moduleId, {
          title: t("admin.lms.section.defaultTitle", { n: sorted.length + 1 }),
        }),
      { refresh: false }
    );
    if (res) {
      const sec = res?.data?.data?.section || res?.data?.data;
      onRefresh?.();
      if (sec) setOpenId(sid(sec));
    }
  };

  const move = (i, dir) => {
    const tgt = i + dir;
    if (tgt < 0 || tgt >= sorted.length) return;
    const next = [...sorted];
    [next[i], next[tgt]] = [next[tgt], next[i]];
    run(() => lmsReorderSectionsApi(moduleId, next.map(sid)));
  };

  return (
    <div className="lms-sections">
      {sorted.length === 0 && (
        <p className="text-muted">{t("admin.lms.section.empty")}</p>
      )}

      {sorted.map((s, i) => (
        <SectionRow
          key={sid(s) || i}
          moduleId={moduleId}
          moduleTitle={moduleTitle}
          section={s}
          index={i}
          total={sorted.length}
          open={openId === sid(s)}
          onToggle={() => setOpenId(openId === sid(s) ? null : sid(s))}
          onRefresh={onRefresh}
          onMove={move}
          onAskDelete={() => setConfirm(s)}
          onPreview={
            courseId
              ? () =>
                navigate(
                  `/admin/lms/course/${courseId}/preview?module=${moduleId}&section=${sid(
                    s
                  )}`
                )
              : null
          }
          run={run}
        />
      ))}

      <button className="btn secondary-btn modal-btn mt-2 btn-with-icon" onClick={addSection}>
        <i className="fas fa-plus"></i> {t("admin.lms.section.add")}
      </button>

      {confirm && (
        <ConfirmationModal
          open={!!confirm}
          deleteInfo={[
            `${t("admin.lms.section.section")} : ${t("admin.lms.section.part", {
              n: sorted.findIndex((s) => sid(s) === sid(confirm)) + 1,
            })}`,
          ]}
          description={t("admin.lms.section.confirmDelete")}
          onCloseModal={(val) => {
            const sec = confirm;
            setConfirm(null);
            if (val && sec) {
              if (sorted.length <= 1) {
                globalAlert(
                  "warning",
                  t(
                    "admin.lms.section.minimumRequired",
                    "A module must contain at least one Part."
                  )
                );
                return;
              }
              run(() => lmsDeleteSectionApi(moduleId, sid(sec)));
            }
          }}
        />
      )}
    </div>
  );
};

const SectionRow = ({
  moduleId,
  moduleTitle,
  section,
  index,
  total,
  open,
  onToggle,
  onRefresh,
  onMove,
  onAskDelete,
  onPreview,
}) => {
  const { t } = useTranslation();
  const assets = (section?.assets || []).slice();
  const quiz = section?.quizDoc || (section?.quiz ? { _id: section.quiz } : null);

  return (
    <div className={`lms-section-card ${open ? "open" : ""}`}>
      <button
        type="button"
        className="lms-section-head"
        onClick={onToggle}
        aria-expanded={open}
      >
        <div className="lms-section-head-main">
          <span className="lms-part-badge">
            {t("admin.lms.section.part", { n: index + 1 })}
          </span>
        </div>
        <div className="lms-section-head-meta">
          <span className="lms-q-chip">
            {t("admin.lms.section.assetCount", { count: assets.length })}
          </span>
          {/* Quizzes publish with the course — just show whether one exists. */}
          {quiz ? (
            <span className="lms-badge badge-green">
              <i className="fas fa-check mr-1"></i>
              {t("admin.lms.section.hasQuiz", "Quiz added")}
            </span>
          ) : (
            <span className="lms-badge badge-gray">
              {t("admin.lms.quiz.statusNone")}
            </span>
          )}
          <i className={`fas fa-chevron-${open ? "up" : "down"} ml-1`}></i>
        </div>
      </button>

      {open && (
        <div className="lms-section-body">
          <div className="lms-section-toolbar">
            <button
              className="btn btn-xs"
              disabled={index === 0}
              onClick={() => onMove(index, -1)}
              aria-label={t("admin.lms.quiz.moveUp")}
            >
              <i className="fas fa-arrow-up"></i>
            </button>
            <button
              className="btn btn-xs"
              disabled={index === total - 1}
              onClick={() => onMove(index, 1)}
              aria-label={t("admin.lms.quiz.moveDown")}
            >
              <i className="fas fa-arrow-down"></i>
            </button>
            {onPreview && (
              <button
                className="btn btn-xs"
                onClick={onPreview}
                aria-label={t("admin.lms.preview.preview")}
                title={t("admin.lms.preview.preview")}
              >
                <i className="fas fa-eye"></i>
              </button>
            )}
            <button
              className="btn btn-xs text-danger"
              onClick={onAskDelete}
              aria-label={t("common.delete")}
            >
              <i className="fas fa-trash"></i>
            </button>
          </div>

          <LmsSectionEditor
            moduleId={moduleId}
            moduleTitle={moduleTitle}
            section={section}
            onRefresh={onRefresh}
            uploadTitle={t("admin.lms.section.part", { n: index + 1 })}
          />
        </div>
      )}
    </div>
  );
};

export default LmsSectionsPanel;
